import { useCallback, useEffect, useState } from "react";
import Toggle from "src/components/common/Toggle";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { UPDATE_RESTAURANT } from "src/constants/mutations";
import { GET_CARD_PROCESSING_FEE_SETTINGS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import { failureToast, successToast } from "src/utils/toasts";

interface CardProcessingFeeSettingsState {
  correlationId: string;
  passCardProcessingFeeToCustomer: boolean;
  cardProcessingFeeEnabledByPlatform: boolean;
}

const CardProcessingFeeSettings = () => {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const storeCorrelationId = hasRestaurant?.correlationId;

  const [settings, setSettings] = useState<CardProcessingFeeSettingsState | null>(null);
  const [passFeeToCustomer, setPassFeeToCustomer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!storeCorrelationId) return;

    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_CARD_PROCESSING_FEE_SETTINGS,
        variables: {
          retrieveStoresInput: {
            storeCorrelationIds: [storeCorrelationId],
          },
        },
      });

      const error = data?.errors?.[0];
      if (error) throw new Error(error.message);

      const store = data?.data?.stores?.[0] ?? null;
      if (!store) throw new Error("Store settings were not found.");

      const nextSettings: CardProcessingFeeSettingsState = {
        correlationId: store.correlationId,
        passCardProcessingFeeToCustomer: Boolean(store.passCardProcessingFeeToCustomer),
        cardProcessingFeeEnabledByPlatform: Boolean(store.cardProcessingFeeEnabledByPlatform),
      };

      setSettings(nextSettings);
      setPassFeeToCustomer(nextSettings.passCardProcessingFeeToCustomer);
    } catch (error) {
      console.error("Error fetching card processing fee settings:", error);
      failureToast(error instanceof Error ? error.message : "Failed to load card processing fee settings.");
    } finally {
      setLoading(false);
    }
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    void fetchSettings();
  }, [fetchSettings]);

  const hasChanges = settings
    ? passFeeToCustomer !== settings.passCardProcessingFeeToCustomer
    : false;

  const handleSave = async () => {
    if (!storeCorrelationId) {
      failureToast("No restaurant selected.");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_RESTAURANT,
        variables: {
          updateStoreInput: {
            storeCorrelationId,
            passCardProcessingFeeToCustomer: passFeeToCustomer,
          },
        },
      });

      const error = data?.errors?.[0];
      if (error) throw new Error(error.message);

      successToast("Card processing fee setting saved.");
      await fetchSettings();
    } catch (error) {
      console.error("Error saving card processing fee settings:", error);
      failureToast(error instanceof Error ? error.message : "Failed to save card processing fee setting.");
    } finally {
      setSaving(false);
    }
  };

  if (!storeCorrelationId) {
    return <p className="text-sm text-gray-500">Select a restaurant first.</p>;
  }

  if (loading) {
    return (
      <div className="w-full max-w-[760px] flex justify-center items-center py-12">
        <BtnLoader />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[760px]">
      <div className="bg-white rounded-[16px] px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_20px_rgba(0,0,0,0.08)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Card processing fee</h2>
        <p className="text-[14px] text-[#6b7280] mb-6">
          When enabled, eligible card processing fees configured by EatsBits will be added to the customer&apos;s total at checkout. Fees are shown before payment and on receipts.
        </p>

        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4 rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#111827]">Pass card processing fee to customer</div>
              <div className="mt-1 text-[13px] leading-5 text-[#6b7280]">
                Applies only when EatsBits has enabled a fee for this store and the payment method is eligible.
              </div>
            </div>
            <Toggle
              checked={passFeeToCustomer}
              onChange={setPassFeeToCustomer}
              disabled={saving}
              className="mt-0.5 shrink-0"
            />
          </div>

          {!settings?.cardProcessingFeeEnabledByPlatform && (
            <div className="rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[13px] leading-5 text-[#92400E]">
              EatsBits has not enabled a card processing fee for this store. Turning on the restaurant setting will not add a fee until the platform setting is enabled.
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !hasChanges}
            className="h-10 px-5 rounded-xl bg-[#FD7657] text-white text-sm font-semibold hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20 focus:ring-offset-2 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardProcessingFeeSettings;
